import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { TransitionablePortal } from 'semantic-ui-react';
import classnames from 'classnames/bind';
import { DEFAULT_LARGE_TRANSITION_DURATION_MS } from '../../../constants/statics';
import useBreakpoints from '../../../hooks/useBreakpoints';
import Icon from '../../elementals/icon';
import Animate from '../../elementals/animate';
import CardContainer from '../../elementals/cardContainer';
import styles from './styles.scss';
const cx = classnames.bind(styles);
const MAX_HEADER_HEIGHT_FOR_SMALL_PADDING = 95;
const DEFAULT_COLLAPSED_HEIGHT = 160;
const DEFAULT_EXPANDED_HEIGHT = 580;
const IconWrapper = ({ onClick, active, fullPageExpandOnMobile, className, style = {}, isMobile = false, }) => {
    return (_jsx(Icon, { onClick: onClick, iconStyle: 'light', color: 'gray', size: isMobile ? 'small' : 'large', name: fullPageExpandOnMobile && isMobile ? 'arrow-up-right' : 'angle-left', className: cx('icon', { active }, className), "data-elm-id": 'toggle_expandable_card', style: style }));
};
const ContentWrapper = ({ active, transitionDurationMs, content, contentHeight, className, }) => (_jsx(Animate.FadeInOutWithHeight, { isVisible: active, duration: transitionDurationMs / 2, height: contentHeight, contentAdjustment: 24, style: { overflow: 'hidden' }, children: _jsx("div", { className: cx('content', { active }, className), children: content }) }));
const renderDefaultCard = ({ children, className, elmId = 'card', isMobile = false, borderColor, borderStyle, }) => (_jsx(CardContainer, { elmId: elmId, borderRadius: 'extra-small', dropShadow: isMobile ? 'small' : 'medium', padding: isMobile ? 'none' : 'medium', className: className, borderColor: borderColor, borderStyle: borderStyle, children: children }));
const ExpandableCard = ({ elmId, id = 0, slots, active, onClick, heightHints = {
    collapsed: DEFAULT_COLLAPSED_HEIGHT,
    expanded: DEFAULT_EXPANDED_HEIGHT,
}, bottomMargin = 18, borderColor, borderStyle, transitionDurationMs = DEFAULT_LARGE_TRANSITION_DURATION_MS, hideHeaderWhenExpanded = false, fullPageExpandOnMobile = false, classNames = {}, disableExpansion = false, }) => {
    const breakpoint = useBreakpoints();
    const isMobile = breakpoint === 'mobile';
    const hasCardSlot = Boolean(slots?.card?.());
    const isSmallHeader = heightHints.collapsed < MAX_HEADER_HEIGHT_FOR_SMALL_PADDING;
    const contentHeight = useMemo(() => hideHeaderWhenExpanded
        ? heightHints.expanded
        : heightHints.expanded - heightHints.collapsed, [heightHints.collapsed, heightHints.expanded, hideHeaderWhenExpanded]);
    const iconPaddingClass = isSmallHeader ? 'small-pad' : 'medium-pad';
    const renderContent = useCallback((forceCollapsed) => !hideHeaderWhenExpanded ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: cx('title', classNames.title, {
                    active: active && !forceCollapsed,
                }), children: [slots?.header?.(), onClick && (_jsx(IconWrapper, { onClick: () => onClick?.(id), active: active && !forceCollapsed, className: cx(classNames.icon, iconPaddingClass, {
                            flip: fullPageExpandOnMobile && isMobile,
                        }), fullPageExpandOnMobile: fullPageExpandOnMobile, isMobile: isMobile }))] }), _jsx(ContentWrapper, { active: active && !forceCollapsed, transitionDurationMs: transitionDurationMs, content: slots?.content?.(), contentHeight: contentHeight, className: cx(classNames.icon) }), onClick && (_jsx("div", { "data-elm-id": `click-area-${id}`, className: cx('click-area', iconPaddingClass), onClick: () => onClick?.(id) }))] })) : (_jsxs(_Fragment, { children: [onClick && (_jsx(IconWrapper, { onClick: () => onClick?.(id), active: active && !forceCollapsed, className: cx(classNames.icon, iconPaddingClass, {
                    flip: fullPageExpandOnMobile && isMobile,
                }), fullPageExpandOnMobile: fullPageExpandOnMobile })), _jsx("div", { className: cx('overlap'), children: _jsx(Animate.FadeInOut, { isVisible: !active || forceCollapsed, duration: transitionDurationMs / 2, children: _jsx("div", { className: cx('title', classNames.title), children: slots?.header?.() }) }) }), _jsx("div", { className: cx('overlap'), children: _jsx(ContentWrapper, { active: active && !forceCollapsed, transitionDurationMs: transitionDurationMs, content: slots?.content?.(), contentHeight: contentHeight, className: classNames.content }) }), onClick && (_jsx("div", { "data-elm-id": `click-area-${id}`, className: cx('click-area', iconPaddingClass), onClick: () => !disableExpansion && onClick?.(id) }))] })), [
        active,
        borderColor,
        borderStyle,
        classNames.content,
        classNames.icon,
        classNames.title,
        contentHeight,
        fullPageExpandOnMobile,
        hideHeaderWhenExpanded,
        iconPaddingClass,
        id,
        isMobile,
        onClick,
        slots,
        transitionDurationMs,
        disableExpansion,
    ]);
    const getPartialCardProps = useCallback((forceCollapsed) => {
        return {
            elmId,
            children: renderContent(forceCollapsed),
            className: cx('card', {
                'hiding-header': hideHeaderWhenExpanded,
                'full-screen': fullPageExpandOnMobile && !forceCollapsed && isMobile,
            }, classNames.card),
            isMobile,
            borderColor,
            borderStyle,
        };
    }, [
        borderColor,
        borderStyle,
        classNames.card,
        elmId,
        fullPageExpandOnMobile,
        hideHeaderWhenExpanded,
        isMobile,
        renderContent,
    ]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { style: active && !(fullPageExpandOnMobile && isMobile)
                    ? {
                        minHeight: heightHints.expanded,
                        marginBottom: bottomMargin,
                    }
                    : {
                        height: heightHints.collapsed,
                        marginBottom: bottomMargin,
                    }, children: [!hasCardSlot &&
                        renderDefaultCard(getPartialCardProps(fullPageExpandOnMobile && isMobile)), hasCardSlot &&
                        slots?.card?.(getPartialCardProps(fullPageExpandOnMobile && isMobile))] }), fullPageExpandOnMobile && isMobile && (_jsx(TransitionablePortal, { open: active, transition: { animation: 'fade up', duration: 500 }, children: _jsxs("div", { className: cx('portal'), children: [!hasCardSlot && renderDefaultCard(getPartialCardProps(false)), hasCardSlot && slots?.card?.(getPartialCardProps(false))] }) }))] }));
};
export default ExpandableCard;
//# sourceMappingURL=index.js.map